import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Seo } from "@/components/Seo";
import { ProjectIcon } from "@/components/ProjectIcon";
import { cn } from "@/lib/utils";
import type { Domain, ProjectWithDomains } from "@/lib/types";

export function ProjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeDomain = searchParams.get("domain");
  const [domains, setDomains] = useState<Domain[]>([]);
  const [projects, setProjects] = useState<ProjectWithDomains[]>([]);

  useEffect(() => {
    fetch("/api/domains").then((r) => r.json()).then(setDomains);
  }, []);

  useEffect(() => {
    const url = activeDomain ? `/api/projects?domain=${encodeURIComponent(activeDomain)}` : "/api/projects";
    fetch(url).then((r) => r.json()).then(setProjects);
  }, [activeDomain]);

  const setDomain = (slug: string | null) => {
    if (slug === null) {
      searchParams.delete("domain");
    } else {
      searchParams.set("domain", slug);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const chips = useMemo(
    () => [{ slug: null, name: "All" } as { slug: string | null; name: string }, ...domains.map((d) => ({ slug: d.slug, name: d.name }))],
    [domains],
  );

  return (
    <div className="container mx-auto px-6 py-16">
      <Seo
        title="Our Projects"
        description="Browse projects from Loose Goose Systems — apps, devices, and medical-adjacent work across our three areas of focus."
        path="/projects"
      />
      <div className="text-center mb-10">
        <h1 className="font-display text-4xl font-extrabold tracking-tight mb-3">Our Projects</h1>
        <p className="text-lg text-muted-foreground">Work across software, devices, and medical</p>
      </div>

      <div className="flex flex-wrap justify-center gap-2 mb-10">
        {chips.map((chip) => {
          const isActive = (chip.slug ?? null) === (activeDomain ?? null);
          return (
            <button
              key={chip.slug ?? "all"}
              onClick={() => setDomain(chip.slug)}
              className={cn(
                "px-4 py-1.5 rounded-full text-sm font-medium border transition-colors",
                isActive
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-muted-foreground border-border hover:bg-accent hover:text-foreground",
              )}
            >
              {chip.name}
            </button>
          );
        })}
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto">
        {projects.map((project) => (
          <Link key={project.slug} to={`/projects/${project.slug}`} className="group">
            <Card className="h-full transition-all duration-300 group-hover:-translate-y-1 group-hover:border-teal/60 group-hover:shadow-lg">
              <CardHeader>
                <ProjectIcon icon={project.icon_url} size="text-5xl" className="mb-3" alt={project.name} />
                <CardTitle className="font-display text-xl transition-colors group-hover:text-teal">
                  {project.name}
                </CardTitle>
                <CardDescription className="text-base">{project.tagline}</CardDescription>
              </CardHeader>
              {project.domains.length > 0 && (
                <CardContent className="flex flex-wrap gap-1.5">
                  {project.domains.map((d) => (
                    <Badge key={d.id} domainSlug={d.slug}>
                      {d.icon && <span className="mr-1">{d.icon}</span>}
                      {d.name}
                    </Badge>
                  ))}
                </CardContent>
              )}
            </Card>
          </Link>
        ))}
      </div>
      {projects.length === 0 && (
        <p className="text-center text-muted-foreground mt-8">
          {activeDomain ? "No projects in this domain yet." : "No projects available yet. Check back soon!"}
        </p>
      )}
    </div>
  );
}
